import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Loader from '../../components/Loader';
import Calling from '../../api/Calling';
import { toast } from 'react-toastify';
import generateCaptcha from '../../components/captcha';
import NotFound from '../../components/UI/NotFound';
import { HiOutlinePlus, HiOutlineRefresh, HiOutlineSearch } from 'react-icons/hi';
import { FaEdit, FaTrashAlt, FaCopy, FaToggleOn, FaImages, FaFolder, FaTimesCircle, FaEye, FaDownload, FaVideo, FaImage } from 'react-icons/fa';

const PAGE_LIMIT = 10;
const MEDIA_ACCEPT = 'image/jpeg,image/jpg,image/png,image/webp,video/mp4';
const THUMB_ACCEPT = 'image/jpeg,image/jpg,image/png,image/webp';
// Checkerboard so transparent frame thumbnails read clearly
const CHECKER = {
    backgroundImage: 'linear-gradient(45deg,#e5e7eb 25%,transparent 25%),linear-gradient(-45deg,#e5e7eb 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#e5e7eb 75%),linear-gradient(-45deg,transparent 75%,#e5e7eb 75%)',
    backgroundSize: '12px 12px',
    backgroundPosition: '0 0,0 6px,6px -6px,-6px 0',
};

// How many frames to render per page in the picker (keeps 200-300 frames light)
const FRAMES_PER_PAGE = 18;

// Frame picker: search + client-side pagination + lazy thumbnails + large preview.
// Only a single page (FRAMES_PER_PAGE) of <img> ever mounts, so big frame libraries don't lag.
const FrameSelect = ({ frames, selected, onToggle }) => {
    const [q, setQ] = useState('');
    const [pg, setPg] = useState(1);
    const [onlySel, setOnlySel] = useState(false);
    const [preview, setPreview] = useState(null);

    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        return (frames || []).filter((f) => {
            if (onlySel && !selected.includes(f._id)) return false;
            if (needle && !(f.f_name || '').toLowerCase().includes(needle)) return false;
            return true;
        });
    }, [frames, q, onlySel, selected]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / FRAMES_PER_PAGE));
    const safePage = Math.min(pg, totalPages);
    useEffect(() => { if (pg > totalPages) setPg(totalPages); }, [totalPages, pg]);
    const paged = filtered.slice((safePage - 1) * FRAMES_PER_PAGE, safePage * FRAMES_PER_PAGE);

    if (!frames || frames.length === 0) {
        return <p className="text-xs text-gray-400">No frames yet — add some in Inpix → Frames.</p>;
    }

    return (
        <div className="border border-gray-200 rounded-lg bg-white p-2 space-y-2">
            {/* controls */}
            <div className="flex items-center gap-2">
                <div className="relative flex-1">
                    <HiOutlineSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                    <input value={q} onChange={(e) => { setQ(e.target.value); setPg(1); }} placeholder="Search frames..."
                        className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-main-blue" />
                </div>
                <label className="flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap cursor-pointer select-none">
                    <input type="checkbox" checked={onlySel} onChange={(e) => { setOnlySel(e.target.checked); setPg(1); }} className="h-3.5 w-3.5" />
                    Selected ({selected.length})
                </label>
            </div>

            {/* grid — portrait 9:16 cells (tall + narrow) so frames preview clearly by default */}
            {paged.length > 0 ? (
                <div className="grid grid-cols-10 sm:grid-cols-[repeat(16,minmax(0,1fr))] gap-1.5 max-h-72 overflow-y-auto p-1">
                    {paged.map((f) => {
                        const on = selected.includes(f._id);
                        return (
                            <div key={f._id} title={f.f_name}
                                className={`relative rounded-lg border-2 p-1 transition ${on ? 'border-main-blue ring-2 ring-main-blue/30' : 'border-gray-200 hover:border-gray-300'}`}>
                                <button type="button" onClick={() => onToggle(f._id)} className="block w-full">
                                    <div className="w-full rounded overflow-hidden h-[100px]" style={CHECKER}>
                                        <img src={f.f_thumbnail || f.f_image} alt={f.f_name} loading="lazy"
                                            className="w-full h-full object-contain" />
                                    </div>
                                </button>
                                {on && <span className="absolute -top-1.5 -right-1.5 bg-main-blue text-white rounded-full w-4 h-4 flex items-center justify-center text-[9px] pointer-events-none">✓</span>}
                                <button type="button" onClick={() => setPreview(f)} title="Preview"
                                    className="absolute -bottom-1.5 -left-1.5 bg-white border border-gray-300 text-gray-600 rounded-full w-4 h-4 flex items-center justify-center text-[8px] hover:bg-gray-100 hover:text-main-blue">
                                    <FaEye />
                                </button>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <p className="text-xs text-gray-400 py-3 text-center">No frames match{q ? ` “${q}”` : ''}.</p>
            )}

            {/* pager */}
            <div className="flex items-center justify-between text-xs text-gray-500 pt-1">
                <span>{filtered.length} frame{filtered.length !== 1 ? 's' : ''} · Page {safePage}/{totalPages}</span>
                <div className="flex gap-1.5">
                    <button type="button" onClick={() => setPg((p) => Math.max(1, p - 1))} disabled={safePage <= 1}
                        className="px-2 py-1 border border-gray-300 rounded-md bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">Prev</button>
                    <button type="button" onClick={() => setPg((p) => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages}
                        className="px-2 py-1 border border-gray-300 rounded-md bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
                </div>
            </div>

            {/* large preview overlay */}
            {preview && (
                <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={() => setPreview(null)}>
                    <div className="bg-white rounded-xl p-4 max-w-xs w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-2">
                            <div className="min-w-0">
                                <span className="text-sm font-medium text-gray-800 truncate block">{preview.f_name}</span>
                                <span className="text-[10px] text-gray-400">Preview @ 1080×1920 (9:16)</span>
                            </div>
                            <button type="button" onClick={() => setPreview(null)} className="text-gray-500 hover:text-gray-700 flex-shrink-0"><FaTimesCircle size={18} /></button>
                        </div>
                        {/* 9:16 portrait frame so the overlay reads exactly as it sits on a 1080×1920 template */}
                        <div className="mx-auto rounded-lg overflow-hidden border border-gray-200 w-full" style={{ ...CHECKER, aspectRatio: '9 / 16' }}>
                            <img src={preview.f_image || preview.f_thumbnail} alt={preview.f_name}
                                className="w-full h-full object-contain" />
                        </div>
                        <button type="button" onClick={() => onToggle(preview._id)}
                            className={`mt-3 w-full px-3 py-2 rounded-lg text-sm font-medium transition ${selected.includes(preview._id) ? 'bg-red-50 text-red-600 border border-red-200 hover:bg-red-100' : 'bg-main-blue text-white hover:bg-main-blue/80'}`}>
                            {selected.includes(preview._id) ? 'Remove from template' : 'Connect to template'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// Live composite preview: the uploaded media (image/video) with a connected frame overlaid,
// exactly as it sits on a 1080×1920 status. Default overlay is "None"; admin can pick any
// connected frame to see the composite. Frames not connected don't appear here.
const CompositePreview = ({ mediaUrl, isVideo, frames, selectedIds }) => {
    const [overlayId, setOverlayId] = useState(null); // default: none

    const connected = useMemo(
        () => (frames || []).filter((f) => selectedIds.includes(f._id)),
        [frames, selectedIds]
    );

    // If the chosen overlay frame gets disconnected, fall back to None.
    useEffect(() => {
        if (overlayId && !selectedIds.includes(overlayId)) setOverlayId(null);
    }, [selectedIds, overlayId]);

    if (!mediaUrl) {
        return <p className="text-xs text-gray-400">Upload media to preview it with frames.</p>;
    }

    const overlay = connected.find((f) => f._id === overlayId);

    return (
        <div className="flex gap-3">
            {/* 9:16 composite stage — media fills, frame overlays on top */}
            <div className="relative bg-black/5 rounded-lg overflow-hidden border border-gray-200 flex-shrink-0"
                style={{ aspectRatio: '9 / 16', height: 230 }}>
                {isVideo
                    ? <video src={mediaUrl} muted loop autoPlay playsInline className="absolute inset-0 w-full h-full object-cover" />
                    : <img src={mediaUrl} alt="media" className="absolute inset-0 w-full h-full object-cover" />}
                {overlay && (
                    <img src={overlay.f_image || overlay.f_thumbnail} alt={overlay.f_name}
                        className="absolute inset-0 w-full h-full object-cover pointer-events-none" />
                )}
                <span className="absolute top-1 left-1 bg-black/55 text-white text-[9px] px-1.5 py-0.5 rounded">
                    {overlay ? overlay.f_name : 'No frame'}
                </span>
            </div>

            {/* frame chooser — None + all connected frames */}
            <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 mb-1">Overlay frame · {connected.length} connected</p>
                <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 max-h-[208px] overflow-y-auto pr-1">
                    <button type="button" onClick={() => setOverlayId(null)}
                        className={`h-[64px] rounded-lg border-2 flex items-center justify-center text-[10px] font-medium transition ${!overlayId ? 'border-main-blue ring-2 ring-main-blue/30 text-main-blue' : 'border-gray-200 text-gray-400 hover:border-gray-300'}`}>
                        None
                    </button>
                    {connected.map((f) => (
                        <button type="button" key={f._id} onClick={() => setOverlayId(f._id)} title={f.f_name}
                            className={`h-[64px] rounded-lg border-2 overflow-hidden transition ${overlayId === f._id ? 'border-main-blue ring-2 ring-main-blue/30' : 'border-gray-200 hover:border-gray-300'}`}
                            style={CHECKER}>
                            <img src={f.f_thumbnail || f.f_image} alt={f.f_name} loading="lazy" className="w-full h-full object-contain" />
                        </button>
                    ))}
                </div>
                {connected.length === 0 && (
                    <p className="text-xs text-gray-400 mt-1">Connect frames below to preview them over the media.</p>
                )}
            </div>
        </div>
    );
};

// Download a stored file (blob first, fall back to opening in a new tab)
const downloadFile = async (url, filename) => {
    if (!url) return;
    try {
        const res = await fetch(url, { mode: 'cors' });
        if (!res.ok) throw new Error('network');
        const blob = await res.blob();
        const objUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objUrl; a.download = filename || 'template';
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(objUrl);
    } catch (e) {
        const a = document.createElement('a');
        a.href = url; a.target = '_blank'; a.rel = 'noopener'; a.click();
    }
};

// Load an image with CORS so it can be drawn to a canvas without tainting it.
const loadCorsImage = (src) => new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
});

// Composite a base image + a frame overlay (both 1080×1920) into one JPEG and download it.
// Throws if the canvas gets tainted (CORS) — caller should fall back to layer-by-layer download.
const downloadImageComposite = async (baseUrl, frameUrl, filename) => {
    const [base, frame] = await Promise.all([loadCorsImage(baseUrl), loadCorsImage(frameUrl)]);
    const canvas = document.createElement('canvas');
    canvas.width = base.naturalWidth || REQ_W;
    canvas.height = base.naturalHeight || REQ_H;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(base, 0, 0, canvas.width, canvas.height);
    ctx.drawImage(frame, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((res, rej) =>
        canvas.toBlob((b) => (b ? res(b) : rej(new Error('compose failed'))), 'image/jpeg', 0.92));
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
};

// Composite a video + a frame overlay into a single downloadable video.
// Draws each video frame + the frame image onto a canvas in real time and records the canvas
// (with the original audio track) via MediaRecorder. Output is WebM (browsers can't mux MP4
// without ffmpeg.wasm). Real-time: a 10s clip takes ~10s. Throws on CORS taint — caller falls back.
const downloadVideoComposite = async (videoUrl, frameUrl, filename) => {
    if (typeof MediaRecorder === 'undefined') throw new Error('MediaRecorder unsupported');

    const frameImg = await loadCorsImage(frameUrl);

    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.src = videoUrl;
    video.playsInline = true;
    video.muted = true; // allow autoplay; the audio TRACK is still captured below
    await new Promise((res, rej) => { video.onloadedmetadata = () => res(); video.onerror = () => rej(new Error('video load')); });

    const w = video.videoWidth || REQ_W;
    const h = video.videoHeight || REQ_H;
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');

    const stream = canvas.captureStream(30);
    // graft the source video's audio onto the canvas stream (if available)
    try {
        const vStream = video.captureStream ? video.captureStream() : (video.mozCaptureStream ? video.mozCaptureStream() : null);
        if (vStream) vStream.getAudioTracks().forEach((t) => stream.addTrack(t));
    } catch (e) { /* no audio track — silent composite */ }

    const mime = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus') ? 'video/webm;codecs=vp9,opus'
        : MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus') ? 'video/webm;codecs=vp8,opus'
        : 'video/webm';
    const recorder = new MediaRecorder(stream, { mimeType: mime });
    const chunks = [];
    recorder.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
    const stopped = new Promise((res) => { recorder.onstop = () => res(); });

    let raf = 0;
    const draw = () => {
        ctx.drawImage(video, 0, 0, w, h);
        ctx.drawImage(frameImg, 0, 0, w, h);
        raf = requestAnimationFrame(draw);
    };

    recorder.start();
    await video.play(); // throws/taints here if CORS not allowed
    draw();

    await new Promise((res) => { video.onended = () => res(); });
    cancelAnimationFrame(raf);
    if (recorder.state !== 'inactive') recorder.stop();
    await stopped;

    const blob = new Blob(chunks, { type: 'video/webm' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
};

// Required media dimensions (portrait status size)
const REQ_W = 1080, REQ_H = 1920;

// Read intrinsic dimensions of an image OR video file.
const readDimensions = (file) => new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    if (file.type && file.type.startsWith('video')) {
        const v = document.createElement('video');
        v.preload = 'metadata';
        v.onloadedmetadata = () => { resolve({ w: v.videoWidth, h: v.videoHeight }); URL.revokeObjectURL(url); };
        v.onerror = () => { resolve(null); URL.revokeObjectURL(url); };
        v.src = url;
    } else {
        const img = new Image();
        img.onload = () => { resolve({ w: img.naturalWidth, h: img.naturalHeight }); URL.revokeObjectURL(url); };
        img.onerror = () => { resolve(null); URL.revokeObjectURL(url); };
        img.src = url;
    }
});

// ---- Dropzone: click / drag-drop / paste, with live preview + 1080x1920 validation ----
const MediaDropzone = ({ label, accept, allowVideo, requireSize, file, onPick, hint }) => {
    const [drag, setDrag] = useState(false);
    const [active, setActive] = useState(false); // gates the window paste listener to the hovered/focused zone
    const inputRef = useRef(null);

    const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
    useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);
    const isVideo = file && file.type && file.type.startsWith('video');

    const acceptList = accept.split(',').map((s) => s.trim());

    const accept_ = async (f) => {
        if (!f) return;
        const okType = acceptList.includes(f.type) || (allowVideo && f.type === 'video/mp4');
        if (!okType) { toast.error('Unsupported file type.'); return; }
        if (requireSize) {
            const dim = await readDimensions(f);
            if (!dim) { toast.error('Could not read media dimensions.'); return; }
            if (dim.w !== REQ_W || dim.h !== REQ_H) {
                toast.error(`Only ${REQ_W}×${REQ_H} allowed. This file is ${dim.w}×${dim.h}.`);
                return;
            }
        }
        onPick(f);
    };

    // keep paste handler pointing at the latest closure
    const acceptRef = useRef(accept_);
    acceptRef.current = accept_;
    useEffect(() => {
        if (!active) return;
        const onPaste = (e) => {
            const items = (e.clipboardData && e.clipboardData.items) || [];
            for (const it of items) {
                if (it.kind === 'file') { const f = it.getAsFile(); if (f) { acceptRef.current(f); break; } }
            }
        };
        window.addEventListener('paste', onPaste);
        return () => window.removeEventListener('paste', onPaste);
    }, [active]);

    const onDrop = (e) => {
        e.preventDefault();
        setDrag(false);
        const f = e.dataTransfer.files && e.dataTransfer.files[0];
        if (f) accept_(f);
    };

    return (
        <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">{label}</label>
            <div
                role="button"
                tabIndex={0}
                onClick={() => inputRef.current && inputRef.current.click()}
                onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                onDragLeave={() => setDrag(false)}
                onDrop={onDrop}
                onMouseEnter={() => setActive(true)}
                onMouseLeave={() => setActive(false)}
                onFocus={() => setActive(true)}
                onBlur={() => setActive(false)}
                className={`border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition ${drag ? 'border-main-blue bg-main-blue/5' : 'border-gray-300 hover:border-main-blue/60'}`}
            >
                <input
                    ref={inputRef}
                    type="file"
                    accept={accept}
                    className="hidden"
                    onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; accept_(f); }}
                />
                {previewUrl ? (
                    isVideo
                        ? <video src={previewUrl} controls className="mx-auto max-h-44 rounded-lg border border-gray-200 bg-black/5" />
                        : <img src={previewUrl} alt="preview" className="mx-auto max-h-44 rounded-lg border border-gray-200 object-contain bg-gray-50" />
                ) : (
                    <div className="py-5 text-gray-500">
                        <p className="text-sm font-medium">Drag &amp; drop, click, or paste</p>
                        {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
                    </div>
                )}
            </div>
            {file && <p className="text-xs text-gray-500 mt-1 truncate">{file.name}</p>}
        </div>
    );
};

const TypeBadge = ({ type }) => (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${type === 'video' ? 'bg-purple-100 text-purple-700' : 'bg-sky-100 text-sky-700'}`}>
        {type === 'video' ? <FaVideo className="text-[10px]" /> : <FaImage className="text-[10px]" />}
        {type === 'video' ? 'Video' : 'Image'}
    </span>
);

// ---- Centered Edit Modal ----
const EditModal = ({ template, categories, frames, onClose, onSaved }) => {
    const [name, setName] = useState(template.i_name || '');
    const [catId, setCatId] = useState(template.i_category?._id || template.i_category || '');
    const [orderVal, setOrderVal] = useState(template.i_order ?? 0);
    const [status, setStatus] = useState(template.i_status);
    const [mediaFile, setMediaFile] = useState(null);
    const [thumbFile, setThumbFile] = useState(null);
    const [selFrames, setSelFrames] = useState((template.i_frames || []).map((f) => (typeof f === 'string' ? f : f._id)));
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    const toggleFrame = (id) => setSelFrames((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

    // Composite preview source: new picked media if any, else the existing stored media.
    const newMediaUrl = useMemo(() => (mediaFile ? URL.createObjectURL(mediaFile) : null), [mediaFile]);
    useEffect(() => () => { if (newMediaUrl) URL.revokeObjectURL(newMediaUrl); }, [newMediaUrl]);
    const previewMediaUrl = newMediaUrl || template.i_media;
    const previewIsVideo = mediaFile ? mediaFile.type.startsWith('video') : template.i_type === 'video';

    const handleSave = async () => {
        if (!name?.trim()) { setError('Name is required.'); return; }
        if (!catId) { setError('Category is required.'); return; }
        setSaving(true);
        try {
            const fd = new FormData();
            fd.append('i_name', name.trim());
            fd.append('i_category', catId);
            fd.append('i_order', Number(orderVal) || 0);
            fd.append('i_status', status);
            fd.append('i_frames', JSON.stringify(selFrames));
            if (mediaFile) fd.append('media', mediaFile);
            if (thumbFile) fd.append('thumbnail', thumbFile);

            const resp = await Calling.updateInpixTemplate(template._id, fd);
            if (resp?.status) { toast.success(resp?.message); onSaved(); onClose(); }
            else toast.error(resp?.message);
        } catch (err) {
            toast.error('Something went wrong.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                <div className="p-5 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-main-blue/10 rounded-lg"><FaEdit className="text-main-blue" /></div>
                        <h2 className="text-xl font-semibold text-gray-800">Edit Template</h2>
                    </div>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700"><FaTimesCircle size={22} /></button>
                </div>

                <div className="p-5 space-y-4">
                    {/* Current media */}
                    <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">Current</label>
                        {template.i_type === 'video'
                            ? <video src={template.i_media} poster={template.i_thumbnail} controls className="max-h-44 rounded-lg border border-gray-200 bg-black/5" />
                            : <img src={template.i_thumbnail || template.i_media} alt={template.i_name} className="max-h-44 rounded-lg border border-gray-200 object-contain bg-gray-50" />}
                    </div>

                    <MediaDropzone label="Replace media (optional)" accept={MEDIA_ACCEPT} allowVideo requireSize file={mediaFile} onPick={setMediaFile} hint="1080×1920 only · image or MP4 video. Leave empty to keep current." />
                    <MediaDropzone label="Replace thumbnail (optional)" accept={THUMB_ACCEPT} file={thumbFile} onPick={setThumbFile} hint="Custom poster image. For video, auto-generated if left empty." />

                    <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">Name</label>
                        <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue" placeholder="Template name" />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm font-medium text-gray-700 mb-1 block">Category</label>
                            <select value={catId} onChange={(e) => setCatId(e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue">
                                <option value="">Select category</option>
                                {categories.map((c) => <option key={c._id} value={c._id}>{c.i_name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-700 mb-1 block">Order</label>
                            <input type="number" min="0" value={orderVal} onChange={(e) => setOrderVal(e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue" />
                        </div>
                    </div>

                    <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">Status</label>
                        <label className="relative inline-flex items-center cursor-pointer mt-2">
                            <input type="checkbox" checked={status} onChange={() => setStatus(!status)} className="sr-only peer" />
                            <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-main-blue"></div>
                            <span className="ml-3 text-sm font-medium text-gray-700">{status ? 'Active' : 'Inactive'}</span>
                        </label>
                    </div>

                    <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">Connected frames ({selFrames.length})</label>
                        <FrameSelect frames={frames} selected={selFrames} onToggle={toggleFrame} />
                    </div>

                    <div>
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1"><FaEye className="text-main-blue text-sm" /> Live preview (media + frame)</label>
                        <CompositePreview mediaUrl={previewMediaUrl} isVideo={previewIsVideo} frames={frames} selectedIds={selFrames} />
                    </div>

                    {error && <p className="text-xs text-red-500">{error}</p>}
                </div>

                <div className="p-5 border-t border-gray-200 flex justify-end gap-3">
                    <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50">Cancel</button>
                    <button onClick={handleSave} disabled={saving}
                        className="px-4 py-2 bg-main-blue text-white rounded-lg hover:bg-main-blue/80 flex items-center gap-2 disabled:opacity-50">
                        <FaEdit className="text-sm" />{saving ? 'Updating...' : 'Update'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ---- Centered View Modal ----
const ViewModal = ({ template, onClose }) => {
    const formatDate = (d) =>
        d ? new Date(d).toLocaleString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A';
    const ext = template.i_type === 'video' ? 'mp4' : 'jpg';
    const isVideo = template.i_type === 'video';

    // Connected frames (populated objects from the API) and the chosen overlay (default none).
    const connected = useMemo(
        () => (template.i_frames || []).filter((f) => f && typeof f === 'object' && f._id),
        [template.i_frames]
    );
    const [overlayId, setOverlayId] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const overlay = connected.find((f) => f._id === overlayId);
    const slug = template.i_slug || 'template';

    // Download according to selection:
    //  • No frame      → original media as-is
    //  • Image + frame → composite the two into one JPEG (client canvas)
    //  • Video + frame → can't compose video in-browser, so download video + frame separately
    const handleDownload = async () => {
        setDownloading(true);
        try {
            if (!overlay) {
                await downloadFile(template.i_media, `${slug}.${ext}`);
            } else if (!isVideo) {
                try {
                    await downloadImageComposite(template.i_media, overlay.f_image, `${slug}-${overlay.f_name}.jpg`);
                } catch (e) {
                    toast.info('Could not merge in-browser — downloading media and frame separately.');
                    await downloadFile(template.i_media, `${slug}.jpg`);
                    await downloadFile(overlay.f_image, `${overlay.f_name}.png`);
                }
            } else {
                try {
                    toast.info('Merging video + frame… please wait (real-time).');
                    await downloadVideoComposite(template.i_media, overlay.f_image, `${slug}-${overlay.f_name}.webm`);
                } catch (e) {
                    toast.info('Could not merge in-browser — downloading video and frame separately.');
                    await downloadFile(template.i_media, `${slug}.mp4`);
                    await downloadFile(overlay.f_image, `${overlay.f_name}.png`);
                }
            }
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                <div className="p-5 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-main-blue/10 rounded-lg"><FaEye className="text-main-blue" /></div>
                        <h2 className="text-xl font-semibold text-gray-800">Template Details</h2>
                    </div>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700"><FaTimesCircle size={22} /></button>
                </div>

                <div className="p-5 space-y-4">
                    {/* Composite stage: media + chosen frame overlay (default none) */}
                    <div className="flex gap-3">
                        <div className="relative bg-black/5 rounded-lg overflow-hidden border border-gray-200 flex-shrink-0"
                            style={{ aspectRatio: '9 / 16', height: 260 }}>
                            {isVideo
                                ? <video src={template.i_media} poster={template.i_thumbnail} muted loop autoPlay playsInline className="absolute inset-0 w-full h-full object-cover" />
                                : <img src={template.i_media} alt={template.i_name} className="absolute inset-0 w-full h-full object-cover"
                                    onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />}
                            {overlay && (
                                <img src={overlay.f_image || overlay.f_thumbnail} alt={overlay.f_name}
                                    className="absolute inset-0 w-full h-full object-cover pointer-events-none" />
                            )}
                            <span className="absolute top-1 left-1 bg-black/55 text-white text-[9px] px-1.5 py-0.5 rounded">
                                {overlay ? overlay.f_name : 'No frame'}
                            </span>
                        </div>

                        {/* Frame chooser: None + all connected frames */}
                        <div className="flex-1 min-w-0">
                            <p className="text-xs text-gray-500 mb-1">Frame · {connected.length} connected</p>
                            {connected.length > 0 ? (
                                <div className="grid grid-cols-4 gap-2 max-h-[236px] overflow-y-auto pr-1">
                                    <button type="button" onClick={() => setOverlayId(null)}
                                        className={`h-[64px] rounded-lg border-2 flex items-center justify-center text-[10px] font-medium transition ${!overlayId ? 'border-main-blue ring-2 ring-main-blue/30 text-main-blue' : 'border-gray-200 text-gray-400 hover:border-gray-300'}`}>
                                        None
                                    </button>
                                    {connected.map((f) => (
                                        <button type="button" key={f._id} onClick={() => setOverlayId(f._id)} title={f.f_name}
                                            className={`h-[64px] rounded-lg border-2 overflow-hidden transition ${overlayId === f._id ? 'border-main-blue ring-2 ring-main-blue/30' : 'border-gray-200 hover:border-gray-300'}`}
                                            style={CHECKER}>
                                            <img src={f.f_thumbnail || f.f_image} alt={f.f_name} loading="lazy" className="w-full h-full object-contain" />
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-xs text-gray-400">No frames connected to this template.</p>
                            )}
                        </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 space-y-3">
                        <Row label="Name" value={template.i_name} />
                        <div className="flex items-start gap-2">
                            <div className="w-28 text-sm font-medium text-gray-500 flex-shrink-0">Slug</div>
                            <div className="flex-1 text-sm text-gray-900 font-mono break-all">{template.i_slug}</div>
                            <button onClick={() => { navigator.clipboard.writeText(template.i_slug); toast.success('Slug copied!'); }}
                                className="flex items-center px-2 py-1 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 flex-shrink-0"><FaCopy /></button>
                        </div>
                        <Row label="Type" value={<TypeBadge type={template.i_type} />} />
                        <Row label="Category" value={template.i_category?.i_name || '-'} />
                        <Row label="Frames" value={connected.length} />
                        {template.i_type === 'video' && <Row label="Duration" value={`${template.i_duration ?? 0}s`} />}
                        <Row label="Views" value={template.i_view_count ?? 0} />
                        <Row label="Downloads" value={template.i_download_count ?? 0} />
                        <Row label="Status" value={
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${template.i_status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                {template.i_status ? 'Active' : 'Inactive'}
                            </span>
                        } />
                        <Row label="Created" value={formatDate(template.createdAt)} />
                        <div className="flex items-start gap-2">
                            <div className="w-28 text-sm font-medium text-gray-500 flex-shrink-0">ID</div>
                            <div className="flex-1 text-sm text-gray-500 font-mono break-all">{template._id}</div>
                            <button onClick={() => { navigator.clipboard.writeText(template._id); toast.success('ID copied!'); }}
                                className="flex items-center px-2 py-1 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 flex-shrink-0"><FaCopy /></button>
                        </div>
                    </div>
                </div>

                <div className="p-5 border-t border-gray-200 flex justify-end gap-2">
                    <button onClick={handleDownload} disabled={downloading}
                        className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 flex items-center gap-2 disabled:opacity-50">
                        <FaDownload /> {downloading ? 'Preparing...' : overlay ? `Download + ${overlay.f_name}` : 'Download'}
                    </button>
                    <button onClick={onClose} className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700">Close</button>
                </div>
            </div>
        </div>
    );
};

const Row = ({ label, value }) => (
    <div className="flex items-start">
        <div className="w-28 text-sm font-medium text-gray-500 flex-shrink-0">{label}</div>
        <div className="flex-1 text-sm text-gray-900 break-all">{value}</div>
    </div>
);

const InpixTemplate = () => {
    const [loader, setLoader] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [categories, setCategories] = useState([]);
    const [frames, setFrames] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    // Filters / paging
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [sortBy, setSortBy] = useState('createdAt');
    const [order, setOrder] = useState('desc');

    // Add form
    const [name, setName] = useState('');
    const [catId, setCatId] = useState('');
    const [orderVal, setOrderVal] = useState(0);
    const [status, setStatus] = useState(true);
    const [mediaFile, setMediaFile] = useState(null);
    const [thumbFile, setThumbFile] = useState(null);
    const [selFrames, setSelFrames] = useState([]);
    const [uploadKey, setUploadKey] = useState(0);
    const [error, setError] = useState(null);

    const toggleFrame = (id) => setSelFrames((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

    // Live preview URL for the picked media (revoked on change/unmount)
    const mediaPreviewUrl = useMemo(() => (mediaFile ? URL.createObjectURL(mediaFile) : null), [mediaFile]);
    useEffect(() => () => { if (mediaPreviewUrl) URL.revokeObjectURL(mediaPreviewUrl); }, [mediaPreviewUrl]);

    const [selected, setSelected] = useState([]);
    const [editTemplate, setEditTemplate] = useState(null);
    const [viewTemplate, setViewTemplate] = useState(null);

    const fetchCategories = useCallback(async () => {
        const resp = await Calling.getInpixCategory({ page: 1, limit: 200, sortBy: 'i_order', order: 'asc' });
        if (resp?.status) setCategories(resp?.data || []);
    }, []);

    const fetchFrames = useCallback(async () => {
        const resp = await Calling.getInpixFrame({ page: 1, limit: 500, sortBy: 'f_order', order: 'asc' });
        if (resp?.status) setFrames(resp?.data || []);
    }, []);

    const fetchData = useCallback(async () => {
        setLoader(true);
        try {
            const resp = await Calling.getInpixTemplate({
                page, limit: PAGE_LIMIT, search, category: categoryFilter, status: statusFilter, type: typeFilter, sortBy, order,
            });
            if (resp?.status) {
                setTemplates(resp?.data || []);
                setTotalRecords(resp?.totalRecords || 0);
                setTotalPages(resp?.totalPages || 1);
            } else {
                setTemplates([]); setTotalRecords(0); setTotalPages(1);
                if (resp?.message) toast.error(resp.message);
            }
        } catch (err) {
            toast.error('Failed to fetch templates');
        } finally {
            setLoader(false);
        }
    }, [page, search, categoryFilter, statusFilter, typeFilter, sortBy, order]);

    useEffect(() => { fetchCategories(); }, [fetchCategories]);
    useEffect(() => { fetchFrames(); }, [fetchFrames]);
    useEffect(() => { fetchData(); }, [fetchData]);

    const resetForm = () => {
        setName(''); setCatId(''); setOrderVal(0); setStatus(true);
        setMediaFile(null); setThumbFile(null); setSelFrames([]); setUploadKey((k) => k + 1); setError(null);
    };

    const handleAdd = async (e) => {
        e.preventDefault();
        if (!mediaFile) return setError('Media (image or video) is required.');
        if (!name?.trim()) return setError('Name is required.');
        if (!catId) return setError('Category is required.');
        setLoader(true);
        try {
            const fd = new FormData();
            fd.append('media', mediaFile);
            if (thumbFile) fd.append('thumbnail', thumbFile);
            fd.append('i_category', catId);
            fd.append('i_name', name.trim());
            fd.append('i_order', Number(orderVal) || 0);
            fd.append('i_status', status);
            fd.append('i_frames', JSON.stringify(selFrames));

            const resp = await Calling.addInpixTemplate(fd);
            if (resp?.status) {
                toast.success(resp?.message);
                resetForm(); fetchData(); fetchCategories();
            } else {
                toast.error(resp?.message);
            }
        } catch (err) {
            toast.error('Something went wrong.');
        } finally {
            setLoader(false);
        }
    };

    const handleToggleStatus = async (t) => {
        setLoader(true);
        try {
            const fd = new FormData();
            fd.append('i_status', !t.i_status);
            const resp = await Calling.updateInpixTemplate(t._id, fd);
            if (resp?.status) { toast.success(resp?.message); fetchData(); }
            else toast.error(resp?.message);
        } catch (err) {
            toast.error('Failed to update status.');
        } finally {
            setLoader(false);
        }
    };

    const handleDelete = async () => {
        if (selected.length === 0) return toast.error('Please select at least one template to delete.');
        const check = generateCaptcha('You are about to delete selected templates. Solve this to confirm.');
        if (!check) return toast.error('Incorrect answer.');
        setLoader(true);
        try {
            const resp = await Calling.deleteInpixTemplate({ id: selected });
            if (resp?.status) {
                toast.success(resp?.message);
                setSelected([]); fetchData(); fetchCategories();
            } else {
                toast.error(resp?.message);
            }
        } catch (err) {
            toast.error('Failed to delete templates.');
        } finally {
            setLoader(false);
        }
    };

    const handleSearch = (e) => { e.preventDefault(); setPage(1); setSearch(searchInput.trim()); };
    const toggleSelect = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
    const selectAll = (e) => setSelected(e.target.checked ? templates.map((t) => t._id) : []);
    const copyId = (id) => { navigator.clipboard.writeText(id); toast.success('ID copied!'); };

    return (
        <>
            {loader ? (
                <Loader loader={loader} />
            ) : (
                <div className="w-full min-h-screen px-4 py-6 mt-16 sm:px-6 lg:px-8 lg:ml-[4.4rem]">
                    <div className="mx-auto">
                        {/* Header */}
                        <div className="flex justify-between items-center mb-4">
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Inpix Templates</h1>
                                <p className="mt-1 text-sm text-gray-500">Manage status templates (image / video) under each category</p>
                            </div>
                            <button onClick={fetchData} className="flex items-center gap-2 px-4 py-2 bg-main-blue hover:bg-main-blue/80 rounded-lg text-white shadow-sm">
                                <HiOutlineRefresh className="text-lg" /> <span>Refresh</span>
                            </button>
                        </div>

                        {/* Add Card */}
                        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100 mb-6">
                            <div className="p-5 border-b border-gray-100 flex items-center gap-3">
                                <div className="p-2.5 bg-main-blue/10 rounded-lg"><HiOutlinePlus className="text-main-blue text-xl" /></div>
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-800">Add New Template</h2>
                                    <p className="text-xs text-gray-500 mt-0.5">Image or MP4 video — both are compressed; video gets an auto/uploaded thumbnail</p>
                                </div>
                            </div>

                            <div className="p-5 bg-gray-50/50">
                                <form onSubmit={handleAdd} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div key={`m-${uploadKey}`}>
                                        <MediaDropzone label="Media (image or video) *" accept={MEDIA_ACCEPT} allowVideo requireSize file={mediaFile} onPick={setMediaFile} hint="1080×1920 only · JPG/PNG/WebP image or MP4 video" />
                                    </div>
                                    <div key={`t-${uploadKey}`}>
                                        <MediaDropzone label="Thumbnail (optional)" accept={THUMB_ACCEPT} file={thumbFile} onPick={setThumbFile} hint="For video: custom poster. Auto-generated if empty." />
                                    </div>

                                    <div>
                                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1"><FaImages className="text-main-blue text-sm" /> Name</label>
                                        <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue" placeholder="e.g., Good Morning 1" />
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1"><FaFolder className="text-main-blue text-sm" /> Category</label>
                                            <select value={catId} onChange={(e) => setCatId(e.target.value)}
                                                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue">
                                                <option value="">Select category</option>
                                                {categories.map((c) => <option key={c._id} value={c._id}>{c.i_name}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium text-gray-700 mb-1 block">Order</label>
                                            <input type="number" min="0" value={orderVal} onChange={(e) => setOrderVal(e.target.value)}
                                                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue" />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1"><FaToggleOn className="text-main-blue text-sm" /> Status</label>
                                        <label className="relative inline-flex items-center cursor-pointer mt-2">
                                            <input type="checkbox" checked={status} onChange={() => setStatus(!status)} className="sr-only peer" />
                                            <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-main-blue"></div>
                                            <span className="ml-3 text-sm font-medium text-gray-700">{status ? 'Active' : 'Inactive'}</span>
                                        </label>
                                    </div>

                                    <div className="md:col-span-2">
                                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1"><FaImages className="text-main-blue text-sm" /> Connect frames ({selFrames.length})</label>
                                        <FrameSelect frames={frames} selected={selFrames} onToggle={toggleFrame} />
                                    </div>

                                    <div className="md:col-span-2">
                                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1"><FaEye className="text-main-blue text-sm" /> Live preview (media + frame)</label>
                                        <CompositePreview mediaUrl={mediaPreviewUrl} isVideo={mediaFile?.type?.startsWith('video')} frames={frames} selectedIds={selFrames} />
                                    </div>

                                    {error && <p className="md:col-span-2 text-xs text-red-500">{error}</p>}

                                    <div className="md:col-span-2 flex items-center justify-end gap-3 pt-2">
                                        <button type="button" onClick={resetForm} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50">Reset</button>
                                        <button type="submit" className="px-4 py-2 bg-main-blue text-white rounded-lg hover:bg-main-blue/80 flex items-center gap-2">
                                            <HiOutlinePlus className="text-sm" /> Save Template
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>

                        {/* Toolbar */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-4">
                            <div className="flex flex-col md:flex-row md:items-center gap-3">
                                <form onSubmit={handleSearch} className="flex-1 flex gap-2">
                                    <div className="relative flex-1">
                                        <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                        <input type="text" value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
                                            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue" placeholder="Search by name..." />
                                    </div>
                                    <button type="submit" className="px-4 py-2 bg-main-blue text-white rounded-lg hover:bg-main-blue/80">Search</button>
                                </form>

                                <select value={categoryFilter} onChange={(e) => { setPage(1); setCategoryFilter(e.target.value); }}
                                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue">
                                    <option value="">All Categories</option>
                                    {categories.map((c) => <option key={c._id} value={c._id}>{c.i_name}</option>)}
                                </select>

                                <select value={typeFilter} onChange={(e) => { setPage(1); setTypeFilter(e.target.value); }}
                                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue">
                                    <option value="">All Types</option>
                                    <option value="image">Image</option>
                                    <option value="video">Video</option>
                                </select>

                                <select value={statusFilter} onChange={(e) => { setPage(1); setStatusFilter(e.target.value); }}
                                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue">
                                    <option value="">All Status</option>
                                    <option value="true">Active</option>
                                    <option value="false">Inactive</option>
                                </select>

                                <select value={`${sortBy}:${order}`} onChange={(e) => { const [s, o] = e.target.value.split(':'); setSortBy(s); setOrder(o); setPage(1); }}
                                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue">
                                    <option value="createdAt:desc">Newest</option>
                                    <option value="i_order:asc">Order ↑</option>
                                    <option value="i_name:asc">Name A–Z</option>
                                    <option value="i_view_count:desc">Most Viewed</option>
                                    <option value="i_download_count:desc">Most Downloaded</option>
                                </select>
                            </div>
                        </div>

                        {/* List */}
                        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
                            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 bg-main-blue/10 rounded-lg"><FaImages className="text-main-blue text-xl" /></div>
                                    <div>
                                        <h2 className="text-lg font-semibold text-gray-800">Templates</h2>
                                        <p className="text-xs text-gray-500 mt-0.5">
                                            <span className="px-2 py-0.5 bg-main-blue/10 text-main-blue rounded-full text-xs font-medium">Total: {totalRecords}</span>
                                        </p>
                                    </div>
                                </div>
                                {selected.length > 0 && (
                                    <button onClick={handleDelete} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2">
                                        <FaTrashAlt className="text-sm" /> Delete Selected ({selected.length})
                                    </button>
                                )}
                            </div>

                            <div className="p-5 bg-gray-50/50">
                                {templates.length > 0 ? (
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-100">
                                                <tr>
                                                    <th className="px-4 py-3 text-left">
                                                        <input type="checkbox" className="h-4 w-4 text-main-blue border-gray-300 rounded"
                                                            onChange={selectAll} checked={selected.length === templates.length && templates.length > 0} />
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Preview</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Views</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Downloads</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {templates.map((t) => (
                                                    <tr key={t._id} className="hover:bg-gray-50">
                                                        <td className="px-4 py-4">
                                                            <input type="checkbox" className="h-4 w-4 text-main-blue border-gray-300 rounded"
                                                                onChange={() => toggleSelect(t._id)} checked={selected.includes(t._id)} />
                                                        </td>
                                                        <td className="px-4 py-4">
                                                            <div className="relative w-12 h-12">
                                                                <img src={t.i_thumbnail || t.i_media} alt={t.i_name}
                                                                    className="w-12 h-12 object-cover rounded-md border border-gray-200 bg-gray-50"
                                                                    onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
                                                                {t.i_type === 'video' && (
                                                                    <span className="absolute bottom-0 right-0 bg-black/70 text-white rounded p-0.5"><FaVideo className="text-[8px]" /></span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{t.i_name}</td>
                                                        <td className="px-4 py-4 whitespace-nowrap"><TypeBadge type={t.i_type} /></td>
                                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{t.i_category?.i_name || '-'}</td>
                                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{t.i_view_count ?? 0}</td>
                                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{t.i_download_count ?? 0}</td>
                                                        <td className="px-4 py-4 whitespace-nowrap">
                                                            <label className="relative inline-flex items-center cursor-pointer">
                                                                <input type="checkbox" checked={t.i_status} onChange={() => handleToggleStatus(t)} className="sr-only peer" />
                                                                <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-main-blue"></div>
                                                            </label>
                                                        </td>
                                                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                                                            <div className="flex items-center gap-2">
                                                                <button onClick={() => setViewTemplate(t)} className="flex items-center px-2 py-1 bg-main-blue/10 text-main-blue rounded-md hover:bg-main-blue/20" title="View"><FaEye /></button>
                                                                <button onClick={() => setEditTemplate(t)} className="flex items-center px-2 py-1 bg-indigo-50 text-indigo-600 rounded-md hover:bg-indigo-100" title="Edit"><FaEdit /></button>
                                                                <button onClick={() => downloadFile(t.i_media, `${t.i_slug || 'template'}.${t.i_type === 'video' ? 'mp4' : 'jpg'}`)} className="flex items-center px-2 py-1 bg-amber-100 text-amber-600 rounded-md hover:bg-amber-200" title="Download"><FaDownload /></button>
                                                                <button onClick={() => copyId(t._id)} className="flex items-center px-2 py-1 bg-green-100 text-green-600 rounded-md hover:bg-green-200" title="Copy ID"><FaCopy /></button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>

                                        {/* Pagination */}
                                        <div className="flex items-center justify-between mt-4 px-1">
                                            <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
                                            <div className="flex gap-2">
                                                <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
                                                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">Prev</button>
                                                <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                                                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <NotFound />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {editTemplate && (
                <EditModal
                    template={editTemplate}
                    categories={categories}
                    frames={frames}
                    onClose={() => setEditTemplate(null)}
                    onSaved={() => { fetchData(); fetchCategories(); }}
                />
            )}

            {viewTemplate && (
                <ViewModal template={viewTemplate} onClose={() => setViewTemplate(null)} />
            )}
        </>
    );
};

export default InpixTemplate;
